import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Eye, Trash2, MessageCircle } from "lucide-react";
import { Button } from "../../../shadcn/components/ui/Button";
import { Calendar } from "../../../shadcn/components/ui/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../shadcn/components/ui/Popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../shadcn/components/ui/Table";
import { cn } from "../../../shadcn/lib/utils";
import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../shadcn/components/ui/AlertDialog";

// Main Services Form Component
export function Servicesform() {
  const [services, setServices] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const navigate = useNavigate();
  const [purchasedProperties, setPurchasedProperties] = useState([]);
  const [transportData, setTransportData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState({ type: "", id: "" });

  // Fetch purchased properties
  useEffect(() => {
    const fetchAllData = async () => {
      const authToken = localStorage.getItem("authToken"); // Get the user's token
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      try {
        // Define the API endpoints
        const propertyApi =
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/property/get";
        const serviceApi =
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/service/get";
        const transportApi =
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/transport/get";

        // Make all API requests simultaneously
        const [propertyResponse, serviceResponse, transportResponse] =
          await Promise.all([
            fetch(propertyApi, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token: authToken }),
            }),
            fetch(serviceApi, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token: authToken }),
            }),
            fetch(transportApi, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token: authToken }),
            }),
          ]);

        // Parse JSON responses
        const [propertyData, serviceData, transportData] = await Promise.all([
          propertyResponse.json(),
          serviceResponse.json(),
          transportResponse.json(),
        ]);

        // Handle the responses
        if (propertyResponse.ok) {
          setPurchasedProperties(
            propertyData.propertyPurchaseData?.propertypurchase || []
          );
        } else {
          console.error("Error fetching properties:", propertyData.message);
        }

        if (serviceResponse.ok) {
          setServices(serviceData.servicePurchaseData?.servicepurchase || []);
        } else {
          console.error("Error fetching services:", serviceData.message);
        }

        if (transportResponse.ok) {
          setTransportData(
            transportData.transportPurchaseData?.transportpurchase || []
          );
        } else {
          console.error(
            "Error fetching transport data:",
            transportData.message
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Handle chat group creation
  const handleCreateChatGroup = async (propertyOwnerEmail, propertyOwnerName) => {
    const authToken = localStorage.getItem("authToken");
    const currentUserEmail = localStorage.getItem("email"); // Assuming email is stored in localStorage

    if (!authToken || !currentUserEmail) {
      console.error("User not authenticated");
      return;
    }

    try {
      const response = await fetch(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/message/chatroom/create",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            members: [currentUserEmail, propertyOwnerEmail],
            groupName: propertyOwnerName,
            token: authToken,
          }),
        }
      );

      const data = await response.json();
      console.log(response.status)

      if (response.ok || response.status === 409) {
        navigateToMessages(); // Navigate to messages tab regardless of 409 error
      } else {
        console.error("Error creating chat group:", data.message);
      }
    } catch (error) {
      console.error("Error creating chat group:", error);
    }
  };

  // Navigate to messages based on role
  const navigateToMessages = () => {
    const role = localStorage.getItem("userRole"); // Assuming role is stored in localStorage
    if (role === "Service Provider") {
      navigate("/servicedashboard/messages");
    } else if (role === "Managed Care Organizations") {
      navigate("/Managed Care Organization's/messages");
    } else if (role === "Individual") {
      navigate("/Dashboard/messages");
    } else if (role === "Property Owner") {
      navigate("/propertyownerdashboard/messages");
    } else if (role === "Non Profits") {
      navigate("/501 C3 Corporations/messages");
    } else if (role === "Real Estate Professionals") {
      navigate("/Hospital/HospitalSystems/messages");
    } else if (role === "SuperAdmin") {
      navigate("/superadmin/messages");
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteInfo.id) return;

    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      console.error("User not authenticated");
      return;
    }

    let deleteApi = "";
    switch (deleteInfo.type) {
      case "property":
        deleteApi =
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/property/delete";
        break;
      case "service":
        deleteApi =
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/service/delete";
        break;
      case "transport":
        deleteApi =
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/transport/delete";
        break;
      default:
        console.error("Invalid delete type:", deleteInfo.type);
        return;
    }

    try {
      const response = await fetch(deleteApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: authToken,
          servicePurchaseId: deleteInfo.id,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log(`${deleteInfo.type} deleted successfully`, data);

        // Remove from state after deletion
        if (deleteInfo.type === "property") {
          setPurchasedProperties((prev) =>
            prev.filter((item) => item._id !== deleteInfo.id)
          );
        } else if (deleteInfo.type === "service") {
          setServices((prev) =>
            prev.filter((item) => item._id !== deleteInfo.id)
          );
        } else if (deleteInfo.type === "transport") {
          setTransportData((prev) =>
            prev.filter((item) => item._id !== deleteInfo.id)
          );
        }
      } else {
        console.error(`Error deleting ${deleteInfo.type}:`, data.message);
      }
    } catch (error) {
      console.error(`Error deleting ${deleteInfo.type}:`, error);
    } finally {
      setIsDeleteDialogOpen(false); // Close the dialog
      setDeleteInfo({ type: "", id: "" }); // Reset
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="spinner-border text-primary" role="status">
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:p-6 w-[75vw] lg:w-full">
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deleteInfo.type}? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-gray-100 hover:bg-gray-200"
              onClick={() => setDeleteInfo({ type: "", id: "" })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sort By Section */}
      <div className="rounded-lg border bg-card p-4 w-full bg-gray-100">
        <h3 className="mb-4 text-lg font-medium">Sort By</h3>
        <div className="flex flex-col gap-4 sm:flex-row">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal sm:w-[240px]",
                  !fromDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {fromDate ? format(fromDate, "PPP") : <span>From Date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={fromDate}
                onSelect={setFromDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal sm:w-[240px]",
                  !toDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {toDate ? format(toDate, "PPP") : <span>To Date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={toDate}
                onSelect={setToDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Services Table */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center gap-2 border-b p-4">
          <div className="rounded-lg bg-primary/10 p-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-lg font-medium">Services</h3>
        </div>
        <div className="max-w-full overflow-x-auto md:overflow-x-visible">
          <div className="min-w-[600px]">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-gray-200 bg-gray-100">
                  <TableHead className="border-x">Service Name</TableHead>
                  <TableHead className="border-x">Service Type</TableHead>
                  <TableHead className="border-x">Provider</TableHead>
                  <TableHead className="border-x">Price</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchasedProperties.length > 0 ? (
                  purchasedProperties.map((purchase) => (
                    <TableRow key={purchase._id}>
                      <TableCell className="border-x font-medium">
                        {purchase.propertyId?.propertyDetails?.title || "N/A"}
                      </TableCell>
                      <TableCell className="border-x">Property</TableCell>
                      <TableCell className="border-x">
                        {purchase?.propertyId?.propertyDetails?.status || "N/A"}
                      </TableCell>
                      <TableCell className="border-x">
                        ${purchase.propertyId?.propertyDetails?.price || "N/A"}
                      </TableCell>
                      <TableCell className="text-center flex">
                        <div className="flex items-center justify-center lg:gap-10 px-2 w-full">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              navigate(
                                `/dashboard/propertyForm/${purchase.propertyId._id}`
                              )
                            }
                          >
                            <Eye className="h-4 w-4" />
                            <span className="w-full mr-0 lg:mr-11">View</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              handleCreateChatGroup(
                                purchase.propertyOwnerId.email,
                                purchase.propertyOwnerId.name
                              )
                            }
                          >
                            <MessageCircle className="h-4 w-4" />
                            <span className="w-full mr-0 lg:mr-11">Chat</span>
                          </Button>
                        </div>
                        <div className="flex items-center justify-center gap-1 lg:gap-10 px-2 w-full">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setDeleteInfo({
                                type: "service",
                                id: purchase._id,
                              });
                              setIsDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="w-full mr-0 lg:mr-11">Delete</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">
                      No purchased properties found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}